import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Lock } from "@phosphor-icons/react";
import { openHiringFile } from "@/pages/admin/hiring/openHiringFile";
import ScreeningPanel from "@/pages/admin/hiring/ScreeningPanel";
import HireDialog from "@/pages/admin/hiring/HireDialog";

const Block = ({ title, children, testid }) => (
  <section className="rounded-2xl border border-border p-5" data-testid={testid}>
    <h3 className="font-bold">{title}</h3>
    <div className="mt-3 space-y-2 text-sm">{children}</div>
  </section>
);

const Pair = ({ k, v }) => (
  <div className="flex justify-between gap-4">
    <span className="text-muted-foreground font-semibold">{k}</span>
    <span className="font-semibold text-right">{v || "—"}</span>
  </div>
);

const WORK_AUTH_LABEL = { authorized: "Authorised to work in the US",
  authorized_with_sponsorship: "Would need sponsorship", prefer_not_to_answer: "Prefers to discuss directly" };

export default function ApplicantDrawer({ id, meta, onClose, onChanged }) {
  const [a, setA] = useState(null);
  const [note, setNote] = useState("");
  const [message, setMessage] = useState({ body: "", request_information: false });
  const [interview, setInterview] = useState({ at: "", mode: "In person", location: "", interviewer: "", notes: "" });
  const [stage, setStage] = useState("");
  const [stageNote, setStageNote] = useState("");
  const [hiring, setHiring] = useState(false);
  const [priv, setPriv] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/hiring/applications/${id}`);
      setA(data); setStage(data.stage);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); onClose(); }
  }, [id, onClose]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMessage) => {
    setBusy(true);
    try { await fn(); toast.success(okMessage); await load(); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!a) return null;
  const resume = (a.files || []).find((f) => f.kind === "resume");

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="applicant-drawer">
        <DialogHeader>
          <DialogTitle>{a.name} · {a.position_title}</DialogTitle>
        </DialogHeader>

        <div className="grid lg:grid-cols-2 gap-4">
          <Block title="Applicant" testid="applicant-summary">
            <Pair k="Email" v={a.email} />
            <Pair k="Phone" v={a.phone} />
            <Pair k="Lives in" v={[a.city, a.state].filter(Boolean).join(", ")} />
            <Pair k="Preferred name" v={a.preferred_name} />
            <Pair k="Submitted" v={a.submitted_at ? new Date(a.submitted_at).toLocaleString() : "Not submitted"} />
            <Pair k="Stage" v={a.stage_label} />
            <Pair k="Future pool" v={a.pool_consent?.opt_in ? `Yes, consented ${new Date(a.pool_consent.at).toLocaleDateString()}` : "No"} />
            <Pair k="Retention hold" v={a.retention_hold ? "On" : "Off"} />
            {resume && (
              <Button size="sm" variant="outline" className="rounded-full font-bold mt-2" data-testid="open-resume-btn"
                      onClick={() => openHiringFile(resume.id, resume.filename)}>
                <FileText className="mr-1" /> Open original résumé
              </Button>
            )}
            {(a.files || []).filter((f) => f.kind !== "resume").map((f) => (
              <Button key={f.id} size="sm" variant="link" className="px-0 justify-start" data-testid={`open-file-${f.id}`}
                      onClick={() => openHiringFile(f.id, f.filename)}>{f.kind.replace("_", " ")}: {f.filename}</Button>
            ))}
          </Block>

          <Block title="Availability and eligibility" testid="applicant-availability">
            <Pair k="Availability" v={a.availability} />
            <Pair k="Preferred schedule" v={a.preferred_schedule} />
            <Pair k="Earliest start" v={a.earliest_start} />
            <Pair k="Years in child care" v={a.childcare_years} />
            <Pair k="Work authorisation" v={WORK_AUTH_LABEL[a.work_authorization] || a.work_authorization} />
            <Pair k="May contact references" v={a.references_may_contact === true ? "Yes" : a.references_may_contact === false ? "Ask first" : "—"} />
            <Pair k="Acknowledged truthfulness" v={a.acknowledgment?.agreed ? `${a.acknowledgment.typed_name} · ${new Date(a.acknowledgment.at).toLocaleString()}` : "—"} />
          </Block>

          {["experience", "education", "certifications", "references"].map((key) => (
            (a[key] || []).length > 0 && (
              <Block key={key} title={key[0].toUpperCase() + key.slice(1)} testid={`applicant-${key}`}>
                {a[key].map((row, i) => (
                  <div key={`${key}-${i}`} className="border-b border-border pb-2 last:border-0">
                    {Object.entries(row).filter(([, v]) => v).map(([k, v]) => (
                      <p key={k}><span className="text-muted-foreground font-semibold capitalize">{k}: </span>{v}</p>
                    ))}
                  </div>
                ))}
              </Block>
            )
          ))}

          {Object.keys(a.answers || {}).length > 0 && (
            <Block title="Answers to position questions" testid="applicant-answers">
              {Object.entries(a.answers).map(([k, v]) => <p key={k}><span className="text-muted-foreground font-semibold">{k}: </span>{v}</p>)}
            </Block>
          )}

          {a.cover_note && (
            <Block title="Why they want to work here" testid="applicant-cover-note">
              <p className="whitespace-pre-line">{a.cover_note}</p>
            </Block>
          )}

          {(a.other_applications || []).length > 0 && (
            <Block title="Other applications from this person" testid="other-applications">
              {a.other_applications.map((o) => (
                <p key={o.id}>{o.position_title} · {o.stage_label} · {o.submitted_at ? new Date(o.submitted_at).toLocaleDateString() : ""}</p>
              ))}
            </Block>
          )}

          <Block title="Stage history" testid="stage-history">
            {(a.stage_history || []).map((h, i) => (
              <div key={`${h.at}-${i}`} className="border-b border-border pb-2 last:border-0">
                <p className="font-bold">{h.stage}</p>
                <p className="text-muted-foreground">{new Date(h.at).toLocaleString()} · {h.by_name || h.by}</p>
                {h.note && <p>{h.note}</p>}
                {h.reason && <p className="italic">{h.reason}</p>}
              </div>
            ))}
          </Block>
        </div>

        {meta.can.applicants_edit && (
          <div className="grid lg:grid-cols-2 gap-4 mt-4">
            <Block title="Move to a stage" testid="stage-control">
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger data-testid="stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.stages.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              <Textarea value={stageNote} placeholder="Job-related note for the record" className="min-h-20"
                        data-testid="stage-note" onChange={(e) => setStageNote(e.target.value)} />
              <Button disabled={busy || stage === a.stage} className="rounded-full font-bold bg-[#3B82F6]"
                      data-testid="save-stage-btn"
                      onClick={() => act(() => api.post(`/hiring/applications/${id}/stage`, { stage, note: stageNote }), "Stage updated")}>
                Save stage
              </Button>
              <p className="text-xs text-muted-foreground">
                Eligible to start and Hired are blocked until the background screening record says eligible.
              </p>
            </Block>

            <Block title="Reviewer and retention" testid="reviewer-control">
              <Label className="font-bold">Assigned reviewer email</Label>
              <Input defaultValue={a.reviewer || ""} id="reviewer-input" data-testid="reviewer-input"
                     onBlur={(e) => e.target.value !== (a.reviewer || "")
                       && act(() => api.post(`/hiring/applications/${id}/reviewer`, { email: e.target.value }), "Reviewer saved")} />
              <Button variant="outline" className="rounded-full font-bold" data-testid="retention-hold-btn"
                      onClick={() => act(() => api.post(`/hiring/applications/${id}/retention-hold`,
                        { hold: !a.retention_hold, reason: window.prompt("Reason for the retention hold?") || "" }),
                        "Retention hold updated")}>
                {a.retention_hold ? "Release retention hold" : "Place a retention hold"}
              </Button>
              {a.has_private_request && (
                <Button variant="outline" className="rounded-full font-bold" data-testid="view-private-btn"
                        onClick={async () => {
                          try { const { data } = await api.get(`/hiring/applications/${id}/private`); setPriv(data); }
                          catch (e) { toast.error(apiError(e.response?.data?.detail)); }
                        }}>
                  <Lock className="mr-1" /> Open private accommodation request
                </Button>
              )}
              {priv && <p className="bg-amber-50 rounded-xl p-4 whitespace-pre-line" data-testid="private-request-text">{priv.accommodation_request || "Nothing recorded."}</p>}
            </Block>

            <Block title="Interviews" testid="interviews-block">
              {(a.interviews || []).map((i) => (
                <div key={i.id} className="border-b border-border pb-2 last:border-0 flex justify-between gap-3">
                  <span>{new Date(i.at).toLocaleString()} · {i.mode} · {i.interviewer}</span>
                  <Select value={i.status} onValueChange={(v) => act(() => api.patch(`/hiring/interviews/${i.id}`, { status: v }), "Interview updated")}>
                    <SelectTrigger className="h-8 w-36" data-testid={`interview-status-${i.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{["scheduled", "completed", "cancelled", "no_show"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
              <Input type="datetime-local" value={interview.at} data-testid="interview-at"
                     onChange={(e) => setInterview({ ...interview, at: e.target.value })} />
              <Input placeholder="Interviewer" value={interview.interviewer} data-testid="interview-interviewer"
                     onChange={(e) => setInterview({ ...interview, interviewer: e.target.value })} />
              <Input placeholder="Where or how" value={interview.location} data-testid="interview-location"
                     onChange={(e) => setInterview({ ...interview, location: e.target.value })} />
              <Button disabled={busy || !interview.at} variant="outline" className="rounded-full font-bold"
                      data-testid="schedule-interview-btn"
                      onClick={() => act(() => api.post(`/hiring/applications/${id}/interviews`, interview), "Interview scheduled")}>
                Schedule interview
              </Button>
            </Block>

            <Block title="Internal review notes" testid="notes-block">
              {(a.messages || []).filter((m) => m.internal).map((m) => (
                <div key={m.id} className="border-b border-border pb-2 last:border-0">
                  <p className="whitespace-pre-line">{m.body}</p>
                  <p className="text-muted-foreground">{m.by_name || m.by} · {new Date(m.at).toLocaleString()}</p>
                </div>
              ))}
              <Textarea value={note} placeholder="Job-related note (never medical, family or protected information)"
                        className="min-h-20" data-testid="note-input" onChange={(e) => setNote(e.target.value)} />
              <Button disabled={busy || !note.trim()} variant="outline" className="rounded-full font-bold"
                      data-testid="add-note-btn"
                      onClick={() => act(async () => { await api.post(`/hiring/applications/${id}/notes`, { body: note }); setNote(""); }, "Note saved")}>
                Save note
              </Button>
            </Block>
          </div>
        )}

        {meta.can.message_applicants && (
          <Block title="Messages with the applicant" testid="applicant-message-block">
            {(a.messages || []).filter((m) => !m.internal).map((m) => (
              <div key={m.id} className={`rounded-xl p-3 ${m.direction === "to_applicant" ? "bg-blue-50" : "bg-slate-50"}`}>
                <p className="font-bold">{m.direction === "to_applicant" ? `${m.by_name || m.by} → applicant` : "Applicant"}
                  <span className="ml-2 text-xs text-muted-foreground">{new Date(m.at).toLocaleString()}</span></p>
                <p className="mt-1 whitespace-pre-line">{m.body}</p>
              </div>
            ))}
            <Textarea value={message.body} placeholder="Message to the applicant" className="min-h-20"
                      data-testid="message-input" onChange={(e) => setMessage({ ...message, body: e.target.value })} />
            <label className="flex gap-2 items-center font-semibold">
              <Checkbox checked={message.request_information} data-testid="request-info-checkbox"
                        onCheckedChange={(v) => setMessage({ ...message, request_information: Boolean(v) })} />
              Also move them to Information Requested
            </label>
            <Button disabled={busy || !message.body.trim()} className="rounded-full font-bold bg-[#3B82F6]"
                    data-testid="send-message-btn"
                    onClick={() => act(async () => { await api.post(`/hiring/applications/${id}/messages`, { ...message, send_email: true }); setMessage({ body: "", request_information: false }); }, "Message sent")}>
              Send message and secure link
            </Button>
          </Block>
        )}

        {meta.can.screening_view && (
          <div className="mt-4">
            <ScreeningPanel id={id} meta={meta} stage={a.stage} onChanged={() => { load(); onChanged(); }} />
          </div>
        )}

        {meta.can.applicants_hire && (
          <div className="mt-4 flex flex-wrap gap-3">
            <Button className="rounded-full font-bold bg-[#10B981] hover:bg-emerald-600" data-testid="start-onboarding-btn"
                    onClick={() => setHiring(true)}>Hire and start onboarding</Button>
            <p className="text-xs text-muted-foreground max-w-md">
              This issues a staff invitation only. No account, permissions, classroom assignment or payroll record is
              created until the person accepts and an administrator assigns them.
            </p>
          </div>
        )}

        {hiring && (
          <HireDialog id={id} meta={meta} applicant={a} onClose={() => setHiring(false)}
                      onHired={() => { setHiring(false); load(); onChanged(); }} />
        )}
      </DialogContent>
    </Dialog>
  );
}
